import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Typography, TextField, Button, Box } from "@mui/material";
import axios from "axios";
import { restUrlProducts } from "./common";
import { useAuth } from "./AuthContext";
import { useTranslation } from "react-i18next";
import Header from "./Header";
import Footer from "./Footer";
import { getSections } from "./common";

function logFormData(formData) {
  console.log("📝 FormData Contents:");
  for (const [key, value] of formData.entries()) {
    if (value instanceof File) {
      console.log(`${key}: [File] ${value.name} (${value.type})`);
    } else {
      console.log(`${key}: ${value}`);
    }
  }
}

export default function ProductForm() {
  const { isAuthenticated } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const { t } = useTranslation();

  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    image: null,
  });
  const [originalData, setOriginalData] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (isEditMode) {
      axios
        .get(`${restUrlProducts}/${id}`)
        .then((res) => {
          const original = {
            title: res.data.title,
            description: res.data.description,
            image: null,
          };
          setFormData({
            title: res.data.title,
            description: res.data.description,
            image: null,
          });
          setOriginalData(original);
          setImagePreview(res.data.image_url);
        })
        .catch((err) => console.error("Failed to load product", err));
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    setSubmitError("");

    if (name === "image") {
      const file = files && files.length > 0 ? files[0] : null;

      setErrors((prev) => ({ ...prev, image: undefined }));

      setFormData((prev) => ({
        ...prev,
        image: file,
      }));

      if (file) {
        setImagePreview(URL.createObjectURL(file));
      }
    } else {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // const fileToBase64 = (file) =>
  //   new Promise((resolve, reject) => {
  //     const reader = new FileReader();
  //     reader.onload = () => resolve(reader.result.split(",")[1]);
  //     reader.onerror = reject;
  //     reader.readAsDataURL(file);
  //   });

  // const handleSubmit = async (e) => {
  //   e.preventDefault();

  //   let imageBase64 = null;
  //   let filename = null;

  //   if (formData.image) {
  //     imageBase64 = await fileToBase64(formData.image);
  //     filename = formData.image.name;
  //   }

  //   const payload = {
  //     title: formData.title,
  //     description: formData.description,
  //     imageBase64,
  //     filename,
  //   };

  //   try {
  //     if (isEditMode) {
  //       await axios.put(`${restUrlProducts}/${id}`, payload, {
  //         headers: { "Content-Type": "application/json" },
  //       });
  //       alert(t("productUpdated"));
  //     } else {
  //       await axios.post(`${restUrlProducts}`, payload, {
  //         headers: { "Content-Type": "application/json" },
  //       });
  //       alert(t("productAdded"));
  //     }
  //     navigate("/");
  //   } catch (err) {
  //     console.error("Save failed", err);
  //     alert(t("failedToSaveProduct"));
  //   }
  // };

  function getChangedFields(formData, originalData) {
    const changed = {};

    for (const key in formData) {
      // Skip undefined values and unchanged ones
      if (formData[key] !== undefined && formData[key] !== originalData[key]) {
        changed[key] = formData[key];
      }
    }

    return changed;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};

    if (!isEditMode) {
      if (!formData.title.trim()) newErrors.title = t("nameRequired");
      if (!formData.description.trim())
        newErrors.description = t("descRequired");
      if (!formData.image) newErrors.image = t("imageRequired");
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const data = new FormData();
    if (isEditMode) {
      const changedFields = getChangedFields(formData, originalData);

      for (const key in changedFields) {
        data.append(key, changedFields[key]);
      }

      if (formData.image) {
        data.append("image", formData.image);
      }

      if (
        !data.has("title") &&
        !data.has("description") &&
        !data.has("image")
      ) {
        setSubmitError(t("noChange"));
        return;
      }
    } else {
      data.append("title", formData.title);
      data.append("description", formData.description);
      data.append("image", formData.image);
    }

    try {
      // PHP does not populate $_POST or $_FILES for PUT, PATCH, or DELETE requests — it only does this for POST. a known quirk in PHP.
      const url = isEditMode
        ? `${restUrlProducts}/${id}`
        : `${restUrlProducts}`;

      await axios.post(url, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert(t(isEditMode ? t("productUpdated") : t("productAdded")));
      navigate("/");
    } catch (err) {
      console.error("Save failed", err?.response?.data || err);
      if (err.response?.data?.error) {
        setSubmitError(err.response.data.error);
      } else {
        setSubmitError(t("failedToSaveProduct"));
      }
    }
  };

  return (
    <Container>
      <Header title={t("title")} sections={getSections(t)} />
      {isAuthenticated && (
        <Typography variant="h4" sx={{ mt: 3, mb: 2 }}>
          {isEditMode ? t("editProduct") : t("addProduct")}
        </Typography>
      )}
      {submitError && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          {submitError}
        </Typography>
      )}

      <form onSubmit={handleSubmit}>
        {isAuthenticated ? (
          <>
            <TextField
              label={t("productName")}
              name="title"
              fullWidth
              margin="normal"
              value={formData.title}
              onChange={handleChange}
              error={Boolean(errors.title)}
              helperText={errors.title}
            />
            <TextField
              label={t("productDesc")}
              name="description"
              fullWidth
              multiline
              rows={6}
              margin="normal"
              value={formData.description}
              onChange={handleChange}
              error={Boolean(errors.description)}
              helperText={errors.description}
            />
          </>
        ) : (
          <>
            <Typography variant="h6" sx={{ mt: 2 }}>
              {t("productName")}:
            </Typography>
            <Typography>{formData.title}</Typography>

            <Typography variant="h6" sx={{ mt: 2 }}>
              {t("productDesc")}:
            </Typography>
            <Typography>{formData.description}</Typography>
          </>
        )}

        {isAuthenticated && (
          <>
            <Button variant="outlined" component="label" sx={{ mt: 2, mb: 1 }}>
              {t("uploadImage")}
              <input
                type="file"
                name="image"
                hidden
                accept="image/*"
                onChange={handleChange}
              />
            </Button>

            {formData.image && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t("selectedFile")}: {formData.image.name}
              </Typography>
            )}
            {errors.image && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {errors.image}
              </Typography>
            )}
          </>
        )}

        {imagePreview && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ mt: 2 }}>
              {t("imagePreview")}:
            </Typography>
            <Box
              component="img"
              src={imagePreview}
              alt="Product"
              sx={{ maxWidth: "100%", height: "auto", borderRadius: 1, mt: 1 }}
            />

            {isAuthenticated && (
              <Button
                variant="text"
                color="error"
                size="small"
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    image: null,
                  }));
                  setImagePreview(null);
                }}
                sx={{ mt: 1 }}
              >
                {t("removeImage")}
              </Button>
            )}
          </Box>
        )}

        {isAuthenticated && (
          <Box sx={{ mt: 3 }}>
            <Button type="submit" variant="contained" sx={{ mr: 2 }}>
              {isEditMode ? t("update") : t("submit")}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/")}
            >
              {t("cancel")}
            </Button>
          </Box>
        )}
      </form>
      <Footer title={t("footerTitle")} description={t("footerDesc")} />
    </Container>
  );
}
